import {
  InfiniteData,
  useMutation,
  useQueryClient
} from "@tanstack/react-query";
import { leadApi } from "api/lead";
import { toast } from "react-toastify";
import { PaginatedResponse } from "shared/types";
import { Lead } from "views/leads-id/types";

interface LeadsTableExtra {
  starred_count?: number;
}

type InfiniteLeads = InfiniteData<PaginatedResponse<Lead>>;

interface ListSnapshot {
  queryKey: readonly unknown[];
  data: PaginatedResponse<Lead>;
}

interface ColumnSnapshot {
  queryKey: readonly unknown[];
  data: InfiniteLeads;
}

interface DetailSnapshot {
  queryKey: readonly unknown[];
  data: Lead;
}

interface MutationContext {
  listSnapshots: ListSnapshot[];
  columnSnapshots: ColumnSnapshot[];
  detailSnapshot: DetailSnapshot | null;
}

const LEADS_TABLE_KEY = "LEADS_TABLE";
const LEADS_PIPELINE_COLUMN_KEY = "LEADS_PIPELINE_COLUMN";
const LEAD_KEY = "LEAD";

const computeStarredCountDelta = (
  previousStarred: boolean | undefined,
  nextStarred: boolean
): number => {
  if (previousStarred === nextStarred) return 0;
  return nextStarred ? 1 : -1;
};

const findPreviousStarred = (
  listCaches: [readonly unknown[], PaginatedResponse<Lead> | undefined][],
  columnCaches: [readonly unknown[], InfiniteLeads | undefined][],
  detail: Lead | undefined,
  leadId: number
): boolean | undefined => {
  const fromList = listCaches
    .flatMap(([, data]) => data?.items ?? [])
    .find((l) => l.id === leadId);
  if (fromList) return fromList.starred;

  const fromColumn = columnCaches
    .flatMap(([, data]) => data?.pages ?? [])
    .flatMap((page) => page.items)
    .find((l) => l.id === leadId);
  if (fromColumn) return fromColumn.starred;

  return detail?.starred;
};

const applyFlipToList = (
  list: PaginatedResponse<Lead>,
  leadId: number,
  nextStarred: boolean,
  starredCountDelta: number
): PaginatedResponse<Lead> => {
  const hasLead = list.items.some((l) => l.id === leadId);
  const items = hasLead
    ? list.items.map((l) =>
        l.id === leadId ? { ...l, starred: nextStarred } : l
      )
    : list.items;

  const extra = (list.extra as LeadsTableExtra | undefined) ?? {};
  const nextStarredCount =
    typeof extra.starred_count === "number"
      ? Math.max(0, extra.starred_count + starredCountDelta)
      : extra.starred_count;

  return {
    ...list,
    items,
    extra: { ...extra, starred_count: nextStarredCount }
  };
};

const applyFlipToInfinite = (
  data: InfiniteLeads,
  leadId: number,
  nextStarred: boolean,
  starredCountDelta: number
): InfiniteLeads => ({
  ...data,
  // starred_count is a per-query total, so apply the delta to the first page
  // only (mirrors total_count) — adding it to every page would over-count if
  // the field is ever summed across pages.
  pages: data.pages.map((page, index) =>
    applyFlipToList(
      page,
      leadId,
      nextStarred,
      index === 0 ? starredCountDelta : 0
    )
  )
});

export const useStarLead = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Lead,
    Error,
    { lead_id: number; starred: boolean },
    MutationContext
  >({
    mutationKey: ["LEAD", "STAR", "UPDATE"],
    mutationFn: ({ lead_id, starred }) =>
      leadApi.starLead({ lead_id, starred }),

    onMutate: async ({ lead_id, starred }) => {
      const detailKey = [LEAD_KEY, String(lead_id)] as const;

      await Promise.all([
        queryClient.cancelQueries({ queryKey: [LEADS_TABLE_KEY] }),
        queryClient.cancelQueries({ queryKey: [LEADS_PIPELINE_COLUMN_KEY] }),
        queryClient.cancelQueries({ queryKey: detailKey })
      ]);

      const listCaches = queryClient.getQueriesData<PaginatedResponse<Lead>>({
        queryKey: [LEADS_TABLE_KEY]
      });
      const columnCaches = queryClient.getQueriesData<InfiniteLeads>({
        queryKey: [LEADS_PIPELINE_COLUMN_KEY]
      });
      const detail = queryClient.getQueryData<Lead>(detailKey);

      // starred_count is a partner-global count from Postgres, so the delta
      // must be computed once from any cache that actually knows the lead's
      // current state — and then applied to every cached list.
      const previousStarred = findPreviousStarred(
        listCaches,
        columnCaches,
        detail,
        lead_id
      );
      const starredCountDelta = computeStarredCountDelta(
        previousStarred,
        starred
      );

      const listSnapshots: ListSnapshot[] = [];
      listCaches.forEach(([queryKey, data]) => {
        if (!data) return;
        listSnapshots.push({ queryKey, data });
        queryClient.setQueryData<PaginatedResponse<Lead>>(
          queryKey,
          applyFlipToList(data, lead_id, starred, starredCountDelta)
        );
      });

      const columnSnapshots: ColumnSnapshot[] = [];
      columnCaches.forEach(([queryKey, data]) => {
        if (!data) return;
        columnSnapshots.push({ queryKey, data });
        queryClient.setQueryData<InfiniteLeads>(
          queryKey,
          applyFlipToInfinite(data, lead_id, starred, starredCountDelta)
        );
      });

      const detailSnapshot = detail
        ? { queryKey: detailKey, data: detail }
        : null;
      if (detail)
        queryClient.setQueryData<Lead>(detailKey, { ...detail, starred });

      return { listSnapshots, columnSnapshots, detailSnapshot };
    },

    onError: (_err, _vars, context) => {
      context?.listSnapshots.forEach(({ queryKey, data }) => {
        queryClient.setQueryData(queryKey, data);
      });
      context?.columnSnapshots.forEach(({ queryKey, data }) => {
        queryClient.setQueryData(queryKey, data);
      });
      if (context?.detailSnapshot)
        queryClient.setQueryData(
          context.detailSnapshot.queryKey,
          context.detailSnapshot.data
        );
      toast.error("Failed to update starred status");
    },

    onSettled: (_data, _err, { lead_id }) => {
      // invalidate, not refetch — active queries will refetch automatically,
      // inactive ones will refetch on next mount. Keeps us from firing N
      // background fetches on every star click.
      queryClient.invalidateQueries({ queryKey: [LEADS_TABLE_KEY] });
      queryClient.invalidateQueries({ queryKey: [LEADS_PIPELINE_COLUMN_KEY] });
      queryClient.invalidateQueries({
        queryKey: [LEAD_KEY, String(lead_id)]
      });
    }
  });
};
